import { useTranslation } from 'react-i18next';
import { TeeSelectableChip } from '@/components/ui/tee-selectable-chip';
import type { IntakeCandidate, IntakeCandidateChoice } from '@/types/intake';

export interface CandidateConfirmStepProps {
  candidates: IntakeCandidate[];
  onChoose: (choice: IntakeCandidateChoice) => void;
  disabled?: boolean;
}

/** D-04: "Did you mean: [Name 1] / [Name 2] / Neither" — click advances immediately. */
export function CandidateConfirmStep({
  candidates,
  onChoose,
  disabled,
}: CandidateConfirmStepProps) {
  const { t } = useTranslation();

  // Disambiguate same-name candidates with their secondaryLabel, matching
  // the existing chip-group pattern in IntakePanel.tsx.
  const nameCounts = candidates.reduce<Record<string, number>>((acc, c) => {
    acc[c.displayName] = (acc[c.displayName] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-[14px] font-medium text-[var(--text-content-title)]">
        {t('intake.candidate.heading')}
      </p>
      <div className="flex flex-wrap gap-2">
        {candidates.map((candidate) => {
          const label =
            nameCounts[candidate.displayName] > 1 && candidate.secondaryLabel
              ? `${candidate.displayName} · ${candidate.secondaryLabel}`
              : candidate.displayName;
          return (
            <TeeSelectableChip
              key={candidate.id}
              label={label}
              selected={false}
              disabled={disabled}
              onClick={() =>
                onChoose({ candidateType: candidate.type, candidateId: candidate.id })
              }
              className="min-h-[44px] md:min-h-0"
            />
          );
        })}
        <TeeSelectableChip
          label={t('intake.candidate.neither')}
          selected={false}
          disabled={disabled}
          onClick={() => onChoose({ candidateType: 'none' })}
          className="min-h-[44px] md:min-h-0"
        />
      </div>
    </div>
  );
}

export default CandidateConfirmStep;
